import { doesEmailExist } from '@/util/user_client';
import { isValidEmail, isValidPhone } from '@/util/regexp';
import { BRAND_AMBASSADOR_CODE_PREFIX } from '@/const/signup';

/** Brand ambassador promo codes may only be used in the ambassador application flow */
export const isBrandAmbassadorCode = (code?: string | null) =>
  !!code?.trim().toLowerCase().startsWith(BRAND_AMBASSADOR_CODE_PREFIX);

const phoneNumberRequired = 'A phone number is required';
const phoneNumberLength = 'Please enter a valid 10-digit phone number';
const validPhoneNumberRequired = 'A valid phone number is required';

/**
 * One in-flight lookup per address, reused for the life of the page.
 *
 * Formik validates the whole form on every blur, and this is the only validator
 * that goes to the network — so tabbing between fields fired a request per field
 * and the answers could land out of order, which is what made errors appear a
 * beat late and flicker. The address is the whole key: the same one always has
 * the same answer within a session, and a new one misses the cache.
 */
const emailLookups = new Map<string, ReturnType<typeof doesEmailExist>>();

function lookupEmail(email: string) {
  const cached = emailLookups.get(email);

  if (cached) {
    return cached;
  }

  const pending = doesEmailExist(email);
  emailLookups.set(email, pending);

  // A failed lookup must not be cached as an answer, or a blip would stick for
  // the rest of the session.
  pending.catch(() => emailLookups.delete(email));

  return pending;
}

export const emailValidationError = async (
  email: string | undefined,
  opts?: { isBrandAmbassadorApp: boolean }
) => {
  const isBrandAmbassadorApp = opts?.isBrandAmbassadorApp;
  if (!email) {
    return 'An email address is required';
  } else if (!isValidEmail(email)) {
    return 'A valid email address is required';
  } else {
    const { userExists, status } = await lookupEmail(email);
    if (status?.match(/cancelled/i)) {
      return 'Welcome back!';
    }
    if (userExists) {
      if (isBrandAmbassadorApp) {
        return "Looks like you're already a member. Please contact influencer@vivrelle.com to submit your application.";
      }
      return 'This email is already registered.';
    }
  }
};

export const phoneValidationError = (phoneNumber: string) => {
  if (!phoneNumber) {
    return phoneNumberRequired;
  } else if (phoneNumber.length < 14) {
    /* Phone totals 14 with masked characters */
    return phoneNumberLength;
  } else if (!isValidPhone(phoneNumber)) {
    return validPhoneNumberRequired;
  }
};

export const phoneOptInValidationError = (
  optinText: boolean,
  phoneNumber: string
) => {
  if (optinText && !phoneNumber) {
    return phoneNumberRequired;
  } else if (optinText && phoneNumber.length < 14) {
    return phoneNumberLength;
  } else if (optinText && !isValidPhone(phoneNumber)) {
    return validPhoneNumberRequired;
  }
};
